/**
 * PurchaseInvoiceV2Screen
 * Pixel-perfect match to Purchase_Invoice.html & Purchase_Invoice_Return.html
 * Screen IDs (from USER_SCREENS table):
 *   4.10.3 = Purchase Invoice          (primary)
 *   4.10.4 = Purchase Returns
 *   4.10.1 = Purchase Invoice Agst GRC
 *   4.10.2 = Purchase Invoice Agst PO
 *   4.8.17 = Purchase Invoice Return (New UI)
 *
 * Features: F2/F3 New Item modal, F10 Size Config modal, F8 Save,
 *   GST row colour-coding, dynamic column panel, inspector panel,
 *   GetInfo lookups, autocomplete dropdowns, Bill/Tax/Amount footer.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Download, Columns2, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import ERPAdvancedProcGrid from '../../../components/procurement/ERPAdvancedProcGrid';
import NewItemModal from '../../../components/procurement/NewItemModal';
import ProcItemGrid from '../../../components/procurement/ProcItemGrid';
import SizeConfigModal from '../../../components/procurement/SizeConfigModal';
import { ItemHistoryModal, SupplierHistoryModal, SplitBarcodeModal } from '../../../components/procurement/ProcRowModals';
import ImageManagerModal from '../../../components/procurement/ImageManagerModal';
import UnitIdentifierModal from '../../../components/procurement/UnitIdentifierModal';
import { loadImages } from '../../../lib/imageStorage';
import GetInfoPopup from '../../../components/GetInfoPopup';
import PurchaseInvoicePrint from './PurchaseInvoicePrint';
import './PurchaseInvoiceV2.css';
import { API_BASE, getToken } from '../../../lib/api';

// ─── Screen ID → config ────────────────────────────────────────────────────
const RETURN_IDS  = new Set(['4.10.4','4.8.17','PURCHASE_RETURNS_SCREEN','PI_RETURN_NEW']);
const AGST_GRC_IDS = new Set(['4.10.1','PURCHASE_INVOICE_AGST_GRC_SCREEN']);
const AGST_PO_IDS  = new Set(['4.10.2','PURCHASE_INVOICE_AGST_PO_SCREEN']);

function getConfig(sid) {
  if (RETURN_IDS.has(sid)) return {
    sid, isReturn: true,
    title: 'Purchase Invoice Return',
    crumb: 'Procurement › Purchase Returns & Debit Notes › Purchase Invoice Return',
    tab1:'General', t1k:'F3', tab2:'Against P.O', t2k:'F4',
    docLabel:'Return No', prevValue:'PR101N0004',
    gFields:[
      {id:'retDate',   label:'Return Date',       type:'date'},
      {id:'retRef',    label:'Return Ref',         type:'text'},
      {id:'againstRef',label:'Against Invoice No', type:'lkp',  si:{screenId:'4.10.3',controlId:'1'}, lt:'Select Invoice'},
      {id:'invDate',   label:'Invoice Date',       type:'date'},
      {id:'payMode',   label:'Pay Mode',           type:'sel',  opts:['Credit','Cash','NEFT','RTGS','UPI']},
      {id:'supplier',  label:'Supplier',            type:'lkp',  si:{screenId:'3.2',  controlId:'5'}, lt:'Select Supplier'},
      {id:'purchAc',   label:'Purchase A/C',        type:'lkp',  si:{screenId:'4.8.1',controlId:'3'}, lt:'Purchase Account'},
      {id:'reason',    label:'Reason For Return',   type:'text'},
      {id:'remarks',   label:'Remarks',             type:'text'},
    ],
    poFields:[
      {id:'apoDate',   label:'A.P.O Date',          type:'date'},
      {id:'mrRef',     label:'M.R/L.R Ref',         type:'text'},
      {id:'poRef',     label:'P.O',                 type:'lkp',  si:{screenId:'4.4.2',controlId:'1'}, lt:'Select PO'},
      {id:'invDate',   label:'Invoice Date',        type:'date'},
      {id:'payMode',   label:'Pay Mode',            type:'sel',  opts:['Credit','Cash','NEFT','RTGS','UPI']},
      {id:'supplier',  label:'Supplier',            type:'lkp',  si:{screenId:'3.2',  controlId:'5'}, lt:'Select Supplier'},
      {id:'purchAc',   label:'Purchase A/C',        type:'lkp',  si:{screenId:'4.8.1',controlId:'3'}, lt:'Purchase Account'},
      {id:'remarks',   label:'Remarks',             type:'text'},
      {id:'grcInfo',   label:'GRC Information',     type:'text'},
    ],
  };
  const tab1 = AGST_PO_IDS.has(sid) ? 'Against P.O' : AGST_GRC_IDS.has(sid) ? 'Against GRC' : 'Against GRN';
  return {
    sid, isReturn: false,
    title: 'Purchase Invoice',
    crumb: 'Procurement › Purchase Accounts › Purchase Invoice',
    tab1, t1k:'F3', tab2:'Against P.I', t2k:'F4',
    docLabel:'Invoice No', prevValue:'PI101N0004',
    gFields:[
      {id:'invDate',   label:'Invoice Date',      type:'date'},
      {id:'invRef',    label:'Invoice Ref',        type:'text'},
      {id:'againstRef',label:'Against GRN No',     type:'lkp',  si:{screenId:'4.8.1',controlId:'7'}, lt:'Select GRN'},
      {id:'grnDate',   label:'GRN Date',           type:'date'},
      {id:'payMode',   label:'Pay Mode',           type:'sel',  opts:['Credit','Cash','NEFT','RTGS','UPI']},
      {id:'supplier',  label:'Supplier',           type:'lkp',  si:{screenId:'3.2',  controlId:'5'}, lt:'Select Supplier'},
      {id:'purchAc',   label:'Purchase A/C',       type:'lkp',  si:{screenId:'4.8.1',controlId:'3'}, lt:'Purchase Account'},
      {id:'remarks',   label:'Remarks',            type:'text'},
      {id:'invInfo',   label:'Invoice Information',type:'text'},
    ],
    poFields:[
      {id:'piDate',    label:'P.I Date',           type:'date'},
      {id:'lrRef',     label:'L.R Ref',            type:'text'},
      {id:'againstRef',label:'Against P.I No',     type:'lkp',  si:{screenId:'4.10.1',controlId:'1'}, lt:'Select P.I'},
      {id:'invDate',   label:'Invoice Date',       type:'date'},
      {id:'payMode',   label:'Pay Mode',           type:'sel',  opts:['Credit','Cash','NEFT','RTGS','UPI']},
      {id:'supplier',  label:'Supplier',           type:'lkp',  si:{screenId:'3.2',  controlId:'5'}, lt:'Select Supplier'},
      {id:'purchAc',   label:'Purchase A/C',       type:'lkp',  si:{screenId:'4.8.1',controlId:'3'}, lt:'Purchase Account'},
      {id:'remarks',   label:'Remarks',            type:'text'},
      {id:'invInfo',   label:'Invoice Information',type:'text'},
    ],
  };
}

// ─── Grid columns ──────────────────────────────────────────────────────────
const CORE_COLS = [
  {id:'code',   label:'ItemCode',    w:90,  num:false},
  {id:'hsn',    label:'HSN',         w:72,  num:false},
  {id:'name',   label:'Item Name',   w:160, num:false, ro:true},
  {id:'artCode',label:'Article Code',w:80,  num:false},
  {id:'oemCode',label:'OEM Code',    w:68,  num:false},
  {id:'uom',    label:'UOM',         w:50,  num:false},
  {id:'qty',    label:'Qty',         w:55,  num:true,  req:true},
  {id:'rate',   label:'Rate',        w:68,  num:true,  req:true},
  {id:'price',  label:'Price',       w:75,  num:true,  ro:true},
  {id:'mrp',    label:'MRP',         w:68,  num:true},
  {id:'wsp',    label:'WSP Rate',    w:68,  num:true},
  {id:'disc',   label:'Disc',        w:55,  num:true},
  {id:'pn',     label:'P/N',         w:38,  num:false},
  {id:'cgst',   label:'CGST%',       w:52,  num:true},
  {id:'sgst',   label:'SGST%',       w:52,  num:true},
  {id:'amount', label:'Amount',      w:88,  num:true,  ro:true},
];
// EXTRA_COLS must NOT duplicate any id already in CORE_COLS (artCode/oemCode/uom/mrp/wsp/disc are core)
const CORE_COL_IDS = new Set(CORE_COLS.map(c=>c.id));
const EXTRA_COLS = [
  {id:'barcode',   label:'Barcode / OEM Code'},
  {id:'division',  label:'Division'},      {id:'dept',       label:'Dept'},
  {id:'category',  label:'Category'},      {id:'subcatg',    label:'Sub Catg'},
  {id:'brand',     label:'Brand'},         {id:'style',      label:'Style'},
  {id:'size',      label:'Size'},          {id:'color',      label:'Color'},
  {id:'design',    label:'Design'},        {id:'smToken',    label:'SM Token'},
  {id:'commission',label:'Commission %'},  {id:'scheme',     label:'Scheme'},
  {id:'batchNo',   label:'Batch No'},      {id:'mfgDate',    label:'Mfg Date'},
  {id:'expiryDate',label:'Expiry Date'},   {id:'remarks',    label:'Remarks'},
].filter(c => !CORE_COL_IDS.has(c.id));

const BLANK = () => ({
  _id: Date.now()+Math.random(), gstType:'excl',
  code:'', hsn:'', name:'', artCode:'', oemCode:'', uom:'',
  qty:'', rate:'', price:'', mrp:'', wsp:'', disc:'0', pn:'P',
  cgst:'2.50', sgst:'2.50', amount:'', barcode:'',
  division:'', dept:'', category:'', subcatg:'', brand:'', style:'',
  size:'', color:'', design:'', cref:'',
  smToken:'', commission:'0.00', scheme:'', batchNo:'', mfgDate:'', expiryDate:'', remarks:'',
});

// GetInfo bindings for classification inspector fields
const CLS_LKP = {
  division:'5.5.8/1',  dept:'5.5.11/1',  category:'5.5.2/1',
  subcatg:'5.5.12/1',  brand:'5.5.3/1',  style:'5.5.10/1',
  size:'5.5.9/1',      color:'5.5.4/1',  design:'5.5.5/1',
};
function parseSI(str) {
  const [screenId, controlId] = str.split('/');
  return { screenId, controlId };
}

// Autofill data for classification dropdowns (same as HTML)
const AUTOFILL = {
  Division:['Textiles','Electronics','Footwear','Cosmetics','Jewellery','Pharma','Grocery','Home Decor','Toys','Stationery','Sports','Furniture','Books'],
  Dept:['Mens','Womens','Kids','Unisex','Infants','Accessories','Home'],
  Category:['Shirts','Trousers','Sarees','T-Shirts','Jackets','Footwear','Bags','Watches','Kurtas','Dresses'],
  Subcatg:['P Sarees','Casual','Formal','Sports'],
  Brand:['Nike','Adidas','Puma','H&M','Zara','Levi\'s'],
  Style:['Regular','Slim Fit','Loose Fit','Classic'],
  Size:['XS','S','M','L','XL','XXL','28','30','32','34','36','38','40'],
  Color:['Red','Blue','Green','Black','White','Grey','Yellow','Pink','Orange'],
  Design:['Solid','Striped','Printed','Checks','Embroidered'],
};

function fmt2(v) { return (parseFloat(v)||0).toFixed(2); }
function fmtIn(v){ return (parseFloat(v)||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2}); }
function nowStr(){ return new Date().toLocaleTimeString('en-IN',{hour12:false}); }
function todayStr(){ return new Date().toISOString().slice(0,10); }
function hdrs(){ const t=getToken(); return {'Content-Type':'application/json',Authorization:t?`Bearer ${t}`:''}; }

function calcRow(r) {
  const qty=parseFloat(r.qty)||0, rate=parseFloat(r.rate)||0, disc=parseFloat(r.disc)||0;
  const cgst=parseFloat(r.cgst)||0, sgst=parseFloat(r.sgst)||0;
  const price=qty*rate;
  const discA=price*disc/100;
  const base=price-discA;
  const gstA=base*(cgst+sgst)/100;
  return {...r, price:price.toFixed(2), amount:(r.gstType==='incl'?price:base+gstA).toFixed(2)};
}

// ─── AutoComplete dropdown ─────────────────────────────────────────────────
function AutoInput({ id, value, dataKey, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ]     = useState(value||'');
  const ref = useRef(null);
  const list = (AUTOFILL[dataKey]||[]).filter(x=>!q||x.toLowerCase().includes(q.toLowerCase())).slice(0,10);
  useEffect(()=>{ setQ(value||''); },[value]);
  useEffect(()=>{
    if(!open) return;
    const h=(e)=>{ if(ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown',h);
    return ()=>document.removeEventListener('mousedown',h);
  },[open]);
  return (
    <div className="pi2-autoinput" ref={ref}>
      <input id={id} value={q} placeholder={placeholder||''}
        autoComplete="off"
        onChange={e=>{ setQ(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={()=>setOpen(true)} />
      <button type="button" className="pi2-lkpbtn" onClick={()=>setOpen(o=>!o)}>🔍</button>
      {open && list.length>0 && (
        <div className="pi2-auto-dd">
          {list.map(x=>(
            <button key={x} type="button" onClick={()=>{ setQ(x); onChange(x); setOpen(false); }}>{x}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Transactions List (landing view) — uses ERPAdvancedProcGrid ───────────
function PIList({ screenId, onNewEntry, onEditEntry, onBack }) {
  const cfg = getConfig(screenId);
  const cols = cfg.isReturn
    ? [
        { field:'retNo',      label:'RETURN NO' },
        { field:'retDate',    label:'DATE' },
        { field:'supplier',   label:'SUPPLIER NAME' },
        { field:'againstRef', label:'AGAINST INVOICE' },
        { field:'items',      label:'ITEMS' },
        { field:'netAmt',     label:'NET AMOUNT' },
        { field:'status',     label:'STATUS' },
      ]
    : [
        { field:'invNo',    label:'PI NO' },
        { field:'refNo',    label:'REF NO' },
        { field:'invDate',  label:'INV DATE' },
        { field:'transDate',label:'TRANS DATE' },
        { field:'supplier', label:'SUPPLIER NAME' },
        { field:'city',     label:'CITY' },
        { field:'qty',      label:'QTY' },
      ];
  return (
    <ERPAdvancedProcGrid
      title={cfg.title}
      screenId={cfg.sid}
      columns={cols}
      records={[]}
      itemCols={[{field:'barcode',label:'BARCODE'},{field:'itemName',label:'ITEM NAME'},{field:'qty',label:'QTY'}]}
      onAddNew={onNewEntry}
      onBack={onBack}
      onEdit={onEditEntry}
      onView={onEditEntry}
      onDelete={r=>alert(`Delete ${r.invNo||r.retNo}?`)}
      onPrint={r=>alert(`Print ${r.invNo||r.retNo}`)}
    />
  );
}

// ─── MAIN FORM COMPONENT ────────────────────────────────────────────────────
function PIFormContent({ screenId, theme, navigateTo, onBack }) {
  const cfg = getConfig(screenId);
  const [mode,        setMode]       = useState('general');
  const [hdr,         setHdr]        = useState({});
  const [suppInfo,    setSuppInfo]   = useState(null);
  const [rows,        setRows]       = useState([
    {...BLANK(), _id:1, code:'1127000143', hsn:'62031990', name:'Printed Shirt', artCode:'A0000143', oemCode:'1000', uom:'Qty', qty:'1', rate:'150.00', price:'150.00', mrp:'799.00', wsp:'0.00', disc:'150', pn:'P', cgst:'2.50', sgst:'2.50', amount:'150.00', division:'Textiles', dept:'Mens', category:'Shirts', gstType:'excl'},
    {...BLANK(), _id:2, code:'1127000144', hsn:'62031990', name:'Solid Shirt',   artCode:'A0000144', oemCode:'1001', uom:'Qty', qty:'2', rate:'150.00', price:'300.00', mrp:'799.00', wsp:'0.00', disc:'0',   pn:'P', cgst:'2.50', sgst:'2.50', amount:'300.00', division:'Textiles', dept:'Mens', category:'Shirts', gstType:'incl'},
  ]);
  const [selIdx,      setSelIdx]     = useState(null);
  const [insTab,      setInsTab]     = useState('cls');
  const [insVals,     setInsVals]    = useState({});
  const [bill,        setBill]       = useState({discPct:'0.00',freight:'0.00',roundOff:'0.00',freightGst:false});
  const [popup,       setPopup]      = useState(null);  // GetInfo popup
  const [showNewItem, setShowNewItem]= useState(false);
  const [showSizeCfg, setShowSizeCfg]= useState(false);
  const [editRow,     setEditRow]    = useState(null);
  const [histModal,   setHistModal]  = useState(null);
  const [imeiModal,   setImeiModal]  = useState(null);
  const [imgModal,    setImgModal]   = useState(null);
  const [imeiModal2,  setImeiModal2] = useState(null);
  const [importOpen,  setImportOpen] = useState(false);
  const [showPrint,   setShowPrint]  = useState(false);
  const [rdCollapsed, setRdCollapsed]= useState(false);
  const [toast,       setToast]      = useState('');
  const [toastVis,    setToastVis]   = useState(false);
  const [clock,       setClock]      = useState(nowStr());

  const toastRef  = useRef(null);

  const curFields = mode==='general' ? cfg.gFields : cfg.poFields;
  const selRow = selIdx!==null ? rows[selIdx] : null;

  useEffect(()=>{ const t=setInterval(()=>setClock(nowStr()),1000); return ()=>clearInterval(t); },[]);

  // Keyboard shortcuts
  useEffect(()=>{
    const h=(e)=>{
      if(e.key==='F2'){ e.preventDefault(); setEditRow(null); setShowNewItem(true); }
      if(e.key==='F3'){ e.preventDefault(); setEditRow(selRow||null); setShowNewItem(true); }
      if(e.key==='F10'){ e.preventDefault(); setShowSizeCfg(true); }
      if(e.key==='F9') { e.preventDefault(); setShowPrint(true); }
      if(e.key==='F8') { e.preventDefault(); handleSave(); }
      if(e.ctrlKey&&e.key==='F2'){ e.preventDefault(); if(selRow)setImgModal({row:selRow,idx:selIdx}); else showToast('Select a row first'); }
      if(e.ctrlKey&&e.key==='F3'){ e.preventDefault(); if(selRow)setImeiModal2(selRow); else showToast('Select a row first'); }
      if(e.key==='Escape'){
        e.preventDefault();
        if(showNewItem){ setShowNewItem(false); return; }
        if(showSizeCfg){ setShowSizeCfg(false); return; }
        if(popup){ setPopup(null); return; }
        if(selIdx!==null){ setSelIdx(null); setInsVals({}); return; }
        if(onBack) onBack();
      }
    };
    window.addEventListener('keydown',h); return ()=>window.removeEventListener('keydown',h);
  });

  // ── Totals ──
  const totQty    = rows.reduce((s,r)=>s+(parseFloat(r.qty)||0),0);
  const totAmount = rows.reduce((s,r)=>s+(parseFloat(r.amount)||0),0);
  const totDisc   = rows.reduce((s,r)=>s+(parseFloat(r.qty)||0)*(parseFloat(r.rate)||0)*(parseFloat(r.disc)||0)/100,0);
  const totGST    = rows.reduce((s,r)=>{
    const base=(parseFloat(r.qty)||0)*(parseFloat(r.rate)||0)*(1-(parseFloat(r.disc)||0)/100);
    return s+base*((parseFloat(r.cgst)||0)+(parseFloat(r.sgst)||0))/100;
  },0);
  const freightGST = bill.freightGst?(parseFloat(bill.freight)||0)*0.18:0;
  const cgstAmt=totGST/2, sgstAmt=totGST/2;
  const grossAmt=totAmount;
  const discAmt=grossAmt*(parseFloat(bill.discPct)||0)/100;
  const netAmt=grossAmt-discAmt+(parseFloat(bill.freight)||0)+freightGST+(parseFloat(bill.roundOff)||0);

  // ── Helpers ──
  const showToast=useCallback((msg)=>{
    setToast(msg); setToastVis(true);
    clearTimeout(toastRef.current);
    toastRef.current=setTimeout(()=>setToastVis(false),2200);
  },[]);

  function updRow(idx,field,value) {
    setRows(prev=>{
      const next=[...prev];
      next[idx]=calcRow({...next[idx],[field]:value});
      return next;
    });
    if(idx===selIdx) setInsVals(v=>({...v,[field]:value}));
  }

  function addEmptyRow() {
    setRows(p=>[...p,BLANK()]);
    showToast('New row added');
  }

  function deleteRow(i) {
    if(rows.length===1){ setRows([BLANK()]); setSelIdx(null); return; }
    setRows(p=>p.filter((_,j)=>j!==i));
    if(selIdx===i) setSelIdx(null);
    else if(selIdx>i) setSelIdx(selIdx-1);
  }

  function selectRow(i) {
    setSelIdx(i);
    const r=rows[i];
    setInsVals({...r});
    setInsTab('cls');
  }

  function handleInsChange(k,v) {
    setInsVals(p=>({...p,[k]:v}));
    if(selIdx!==null) updRow(selIdx,k,v);
  }

  function setHdrField(id,display,raw) {
    setHdr(p=>({...p,[id]:display,[`${id}_raw`]:raw??display}));
    if(id==='supplier'&&raw) fetchSupplier(raw);
  }

  async function fetchSupplier(id) {
    try {
      const r=await fetch(`${API_BASE}/api/supplier-master/${id}`,{headers:hdrs()});
      const d=await r.json(); if(d.success) setSuppInfo(d.data);
    } catch(_){}
  }

  function openLookup(si,lt,cb) {
    if(!si){ showToast('No lookup configured'); return; }
    setPopup({si,title:lt,cb});
  }
  function handleLkpSelect(row,cols) {
    const dc=cols.find(c=>c.displayMember)||cols[0];
    const vc=cols.find(c=>c.valueMember)||cols[0];
    const dv=row[dc?.field||Object.keys(row)[0]]||'';
    const vv=row[vc?.field||Object.keys(row)[0]]||'';
    if(popup?.cb) popup.cb(dv,vv,row);
    setPopup(null);
  }

  function handleSave() {
    if(!hdr.supplier){ showToast('Supplier is required'); return; }
    const items=rows.filter(r=>r.code||r.qty);
    if(!items.length){ showToast('Add at least one item'); return; }
    showToast(`${cfg.title} saved! Net ₹${fmtIn(netAmt)}`);
  }

  function handleClear() {
    setHdr({}); setSuppInfo(null);
    setRows([BLANK()]); setSelIdx(null); setInsVals({});
    setBill({discPct:'0.00',freight:'0.00',roundOff:'0.00',freightGst:false});
    showToast('Screen cleared');
  }

  // ── Header field renderer ──
  function renderHdrField(f) {
    const val=hdr[f.id]||'';
    if(f.type==='date') return (
      <div className="pi2-field" key={f.id}>
        <label>{f.label}</label>
        <input type="date" value={val||todayStr()} onChange={e=>setHdrField(f.id,e.target.value)} />
      </div>
    );
    if(f.type==='sel') return (
      <div className="pi2-field" key={f.id}>
        <label>{f.label}</label>
        <select value={val} onChange={e=>setHdrField(f.id,e.target.value)}>
          {(f.opts||[]).map(o=><option key={o}>{o}</option>)}
        </select>
      </div>
    );
    if(f.type==='lkp') return (
      <div className="pi2-field" key={f.id}>
        <label>{f.label}</label>
        <div className="pi2-autoinput">
          <input value={val} onChange={e=>setHdrField(f.id,e.target.value)} data-testid={`hdr-${f.id}`} />
          <button type="button" className="pi2-lkpbtn"
            onClick={()=>openLookup(f.si,f.lt,(d,r)=>setHdrField(f.id,d,r))}>🔍</button>
        </div>
      </div>
    );
    return (
      <div className="pi2-field" key={f.id}>
        <label>{f.label}</label>
        <input value={val} onChange={e=>setHdrField(f.id,e.target.value)} />
      </div>
    );
  }

  // ── Row class ──
  function rowClass(r,i) {
    let cls='';
    if(i===selIdx) return 'selected';
    if(r.gstType==='incl') cls='pi2-gst-incl';
    else cls='pi2-gst-excl';
    if(parseFloat(r.disc)>0) cls+=' pi2-has-disc';
    return cls;
  }

  // ── Inspector tabs ──
  const clsFields=[
    {k:'division',lbl:'Division'},{k:'dept',lbl:'Department'},{k:'category',lbl:'Category'},
    {k:'subcatg',lbl:'Sub Category'},{k:'brand',lbl:'Brand'},{k:'style',lbl:'Style'},
    {k:'size',lbl:'Size'},{k:'color',lbl:'Color'},{k:'design',lbl:'Design'},{k:'cref',lbl:'C.REF'},
  ];

  return (
    <div className="pi2-app" data-testid="purchase-invoice-v2"
      data-dev-component="PIFormContent" data-dev-file="PurchaseInvoiceV2Screen.jsx">

      {/* Title bar */}
      <div className="pi2-titlebar">
        <div>
          <div className="pi2-crumb">
            {onBack&&<span style={{cursor:'pointer',opacity:.7,marginRight:6}} onClick={onBack}>← {cfg.title}</span>}
            {onBack&&<span style={{opacity:.4,marginRight:6}}>›</span>}
            {cfg.crumb}
          </div>
          <div className="pi2-heading-row">
            <h1 className="pi2-h1">{cfg.title}</h1>
            <div className="pi2-modetabs">
              <button className={mode==='general'?'active':''} onClick={()=>setMode('general')} data-testid="tab-general">
                {cfg.tab1} <span className="pi2-fkey">{cfg.t1k}</span>
              </button>
              <button className={mode==='po'?'active':''} onClick={()=>setMode('po')} data-testid="tab-po">
                {cfg.tab2} <span className="pi2-fkey">{cfg.t2k}</span>
              </button>
            </div>
            <div className="pi2-docbadges">
              <div className="pi2-docbadge hi"><span className="dl">{cfg.docLabel}</span><span className="dv">—</span></div>
              <div className="pi2-docbadge"><span className="dl">Previous Trans. No</span><span className="dv">{cfg.prevValue}</span></div>
            </div>
          </div>
        </div>
        <div className="pi2-fkeys">
          <span style={{cursor:'pointer'}} onClick={()=>{setEditRow(null);setShowNewItem(true);}}><b>F2</b> New Item</span>
          <span style={{cursor:'pointer'}} onClick={()=>{setEditRow(selRow||null);setShowNewItem(true);}}><b>F3</b> {selRow?'Edit Row':'New Item'}</span>
          <span style={{cursor:'pointer'}} onClick={()=>setShowSizeCfg(true)}><b>F10</b> Size Config</span>
          <span style={{cursor:'pointer'}} onClick={()=>{if(selRow)setImgModal({row:selRow,idx:selIdx});}}><b>Ctrl+F2</b> Image</span>
          <span style={{cursor:'pointer'}} onClick={()=>{if(selRow)setImeiModal2(selRow);}}><b>Ctrl+F3</b> IMEI/Serial</span>
          <span><b>F8</b> Save</span>
          <span style={{cursor:'pointer'}} onClick={()=>setShowPrint(true)}><b>F9</b> Print</span>
          <span><b>Esc</b> Close</span>
        </div>
      </div>

      {/* Body */}
      <div className="pi2-body">
        {/* Left column */}
        <div className="pi2-leftcol">

          {/* Header zone */}
          <div className="pi2-hdrzone">
            <div className="pi2-card">
              <div className="pi2-cardhead"><span className="pi2-dot" />Document Information
                <button type="button" className="pi2-addfld"><span>+</span>Add Field</button>
              </div>
              <div className="pi2-fieldgrid" data-testid="doc-info-form">
                {curFields.map(f=>renderHdrField(f))}
              </div>
            </div>
            <div className="pi2-card">
              <div className="pi2-cardhead"><span className="pi2-dot" />Supplier Information</div>
              <div className="pi2-suppbox">
                <div className="pi2-supprow"><span className="k">GSTIN</span><span className="v">{suppInfo?.gstin||'—'}</span></div>
                <div className="pi2-supprow"><span className="k">St Code</span><span className="v">{suppInfo?.stateCode||'—'}</span></div>
                <div className="pi2-supprow"><span className="k">O/S</span><span className="v neg">{suppInfo?.outstanding||'—'}</span></div>
                <div className="pi2-supprow"><span className="k">Inv Amt</span><span className="v">{fmtIn(netAmt)}</span></div>
                <div className="pi2-supprow"><span className="k">Bal Amt</span><span className="v">0.00</span></div>
                <div className="pi2-supp-total"><span>Total</span><span className="v">{suppInfo?.outstanding||'—'}</span></div>
              </div>
            </div>
          </div>

          {/* Item grid */}
          <ProcItemGrid
            rows={rows}
            selIdx={selIdx}
            onRowChange={(idx, field, value) => updRow(idx, field, value)}
            onRowSelect={selectRow}
            onRowDelete={deleteRow}
            onRowDuplicate={idx => { setRows(p=>[...p,{...p[idx],_id:Date.now()+Math.random()}]); showToast('Row duplicated'); }}
            onRowEdit={idx => { setEditRow(rows[idx]); setShowNewItem(true); }}
            onRowVoid={idx => { showToast(`Row ${idx+1} voided`); }}
            onAddEmptyRow={addEmptyRow}
            onRowItemHistory={idx => setHistModal({type:'item', row:rows[idx]})}
            onRowSupplierHistory={idx => setHistModal({type:'supplier', row:rows[idx]})}
            onRowSplitBarcode={idx => setImeiModal2(rows[idx])}
            totQty={totQty}
            totAmount={totAmount}
            totDisc={totDisc}
            totGST={totGST}
            roundOff={bill.roundOff}
          />

          {/* Row detail bar */}
          <div className={`pi2-rowdetail${rdCollapsed?' collapsed':''}`} data-testid="row-detail-bar">
            <div className="pi2-rowdetail-head">
              <span className="pi2-dot" />Selected Row — Additional Details
              <span className={`pi2-rowdetail-tag${selRow?' active':''}`}>{selRow?selRow.code||'Row selected':'No row selected'}</span>
              <button type="button" className="pi2-addfld" style={{marginLeft:8}}><span>+</span>Add Field</button>
              <button type="button" className="pi2-collapse-btn" onClick={()=>setRdCollapsed(v=>!v)}>
                {rdCollapsed?'▸ Minimize':'▾ Minimize'}
              </button>
            </div>
            <div className="pi2-rowdetail-fields">
              {[{k:'smToken',lbl:'SM Token'},{k:'commission',lbl:'Commission %'},{k:'scheme',lbl:'Scheme'},
                {k:'batchNo',lbl:'Batch No'},{k:'mfgDate',lbl:'Mfg Date'},{k:'expiryDate',lbl:'Expiry Date'},
                {k:'remarks',lbl:'Remarks',grow:true}].map(f=>(
                <div className={`pi2-rdfield${f.grow?' grow':''}`} key={f.k}>
                  <label>{f.lbl}</label>
                  <input value={insVals[f.k]||''} disabled={!selRow}
                    onChange={e=>handleInsChange(f.k,e.target.value)} />
                </div>
              ))}
            </div>
          </div>

          {/* Bill / Tax / Amount */}
          <div className="pi2-billrow">
            <div className="pi2-card">
              <div className="pi2-cardhead"><span className="pi2-dot" />Bill Details
                <button type="button" className="pi2-addfld"><span>+</span>Add Field</button>
              </div>
              <div className="pi2-billrows">
                {[['discPct','Disc Perc%',true],['freight','Freight',false],['roundOff','Round Off',false]].map(([k,lbl,chk])=>(
                  <div className="pi2-brow" key={k}>
                    <label>{chk?<><input type="checkbox" defaultChecked />{lbl}</>:lbl}</label>
                    <input className="bval pi2-mono" value={bill[k]}
                      onChange={e=>setBill(p=>({...p,[k]:e.target.value}))} />
                    {k==='freight'&&<span className="pi2-gstinline">
                      <input type="checkbox" checked={bill.freightGst}
                        onChange={e=>setBill(p=>({...p,freightGst:e.target.checked}))} />GST
                    </span>}
                    {k==='roundOff'&&<button type="button" className="pi2-plusbtn">+</button>}
                  </div>
                ))}
                {bill.freightGst&&<div className="pi2-brow readout"><label>FreightGST</label><span className="bro-val">{fmt2(freightGST)}</span></div>}
              </div>
            </div>
            <div className="pi2-card">
              <div className="pi2-cardhead"><span className="pi2-dot" />Tax Breakup
                <button type="button" className="pi2-addfld"><span>+</span>Add Field</button>
              </div>
              <div className="pi2-amtlist">
                {[['CGST Amt',fmt2(cgstAmt)],['SGST Amt',fmt2(sgstAmt)],['IGST Amt','0.00'],['UTGST Amt','0.00']].map(([l,v])=>(
                  <div className="r" key={l}><span>{l}</span><span className="pi2-mono">{v}</span></div>
                ))}
              </div>
            </div>
            <div className="pi2-card pi2-amtcard">
              <div className="pi2-cardhead"><span className="pi2-dot" />Amount Details
                <button type="button" className="pi2-addfld"><span>+</span>Add Field</button>
              </div>
              <div className="pi2-amtlist">
                <div className="r"><span>Gross Amount</span><span className="pi2-mono">{fmtIn(grossAmt)}</span></div>
                <div className="r"><span>Discount</span><span className="pi2-mono">{fmtIn(discAmt)}</span></div>
                <div className="r"><span>GST Amount</span><span className="pi2-mono">{fmtIn(totGST)}</span></div>
                <div className="r total"><span>Net Amount</span><span className="pi2-mono">{fmtIn(netAmt)}</span></div>
              </div>
            </div>
          </div>
        </div>{/* /leftcol */}

        {/* Right rail */}
        <div className="pi2-rail">
          <div className="pi2-railtop">
            <button type="button" className="pi2-btn primary" onClick={()=>setShowNewItem(true)} data-testid="new-item-btn">
              ＋ New Item <span className="pi2-kbd">F2 / F3</span>
            </button>
            <div className="pi2-import-wrap">
              <button type="button" className="pi2-btn" onClick={()=>setImportOpen(o=>!o)}>
                ⬆ Import <span style={{fontSize:9,marginLeft:2}}>▾</span>
              </button>
              <div className={`pi2-import-dd${importOpen?' open':''}`}>
                {['📊 Excel (.xlsx)','📄 CSV','📕 PDF','🖨 Scan','📟 PDA'].map(x=>(
                  <button key={x} type="button" onClick={()=>{setImportOpen(false);showToast(`Import ${x} — coming soon`);}}>{x}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Inspector */}
          <div className="pi2-inspector" data-testid="inspector-panel">
            {!selRow ? (
              <div className="pi2-insp-empty">
                <div className="ico">🔍</div>
                <b>No row selected</b>
                <span>Click any item in the grid to view its full classification, pricing &amp; tax properties here.</span>
              </div>
            ) : (
              <>
                <div className="pi2-insp-head">
                  <div className="eyebrow">Item Properties · Selected Row</div>
                  <div className="pi2-headgrid">
                    <div className="pi2-hg-cell"><span className="pi2-hg-k">Code</span><span className="pi2-hg-v">{insVals.code||'—'}</span></div>
                    <div className="pi2-hg-cell"><span className="pi2-hg-k">Article</span><span className="pi2-hg-v">{insVals.artCode||'—'}</span></div>
                    <div className="pi2-hg-cell"><span className="pi2-hg-k">HSN</span><span className="pi2-hg-v">{insVals.hsn||'—'}</span></div>
                    <div className="pi2-hg-cell"><span className="pi2-hg-k">Name</span><span className="pi2-hg-v">{insVals.name||'—'}</span></div>
                  </div>
                </div>
                <div className="pi2-insp-tabs">
                  {[['cls','Classification'],['num','Numeric / Pricing'],['img','Image'],['info','Additional Info']].map(([t,l])=>(
                    <button key={t} className={`pi2-insp-tab${insTab===t?' active':''}`} onClick={()=>setInsTab(t)}>{l}</button>
                  ))}
                </div>
                <div className="pi2-insp-body">
                  {insTab==='cls' && (
                    <div className="pi2-insect">
                      <div className="pi2-sectitle">Classification Attributes <button type="button" className="pi2-addfld" style={{fontSize:9.5}}><span>+</span>Add Field</button></div>
                      {clsFields.map(f=>(
                        <div className="pi2-edrow" key={f.k}>
                          <label>{f.lbl}</label>
                          {CLS_LKP[f.k]
                            ? <div className="pi2-autoinput">
                                <AutoInput value={insVals[f.k]||''} dataKey={f.k.charAt(0).toUpperCase()+f.k.slice(1)}
                                  onChange={v=>handleInsChange(f.k,v)} />
                                <button type="button" className="pi2-lkpbtn"
                                  onClick={()=>openLookup(parseSI(CLS_LKP[f.k]),`Select ${f.lbl}`,(d)=>handleInsChange(f.k,d))}>🔍</button>
                              </div>
                            : <input value={insVals[f.k]||''} onChange={e=>handleInsChange(f.k,e.target.value)} />
                          }
                        </div>
                      ))}
                    </div>
                  )}
                  {insTab==='num' && (
                    <div className="pi2-insect">
                      <div className="pi2-sectitle">Numeric / Pricing</div>
                      {[['qty','Qty'],['rate','Rate'],['mrp','MRP'],['wsp','WSP'],['disc','Disc Amt'],
                        ['cgst','CGST %'],['sgst','SGST %'],['inputTax','Input Tax %'],['outputTax','Output Tax %']].map(([k,lbl])=>(
                        <div className="pi2-edrow" key={k}>
                          <label>{lbl}</label>
                          <input className="mono" value={insVals[k]||''}
                            onChange={e=>handleInsChange(k,e.target.value)} />
                        </div>
                      ))}
                    </div>
                  )}
                  {insTab==='img' && (
                    <div style={{padding:12,display:'flex',flexDirection:'column',alignItems:'center',gap:10}}>
                      {selRow?.code && loadImages(selRow.code).length>0 ? (
                        <div style={{display:'flex',flexWrap:'wrap',gap:6,justifyContent:'center'}}>
                          {loadImages(selRow.code).map((img,i)=>(
                            <img key={i} src={img.small} alt="" style={{width:80,height:80,objectFit:'cover',borderRadius:4,border:'1px solid var(--pi2-border)'}}/>
                          ))}
                        </div>
                      ):(
                        <div style={{fontSize:32,opacity:.4}}>📷</div>
                      )}
                      <button type="button" className="pi2-addfld" style={{margin:'0 auto'}}
                        onClick={()=>setImgModal({row:selRow,idx:selIdx})}>
                        🖼 Upload / Crop / Attach Link
                      </button>
                      <div style={{fontSize:11,color:'var(--pi2-ink-soft)',textAlign:'center'}}>
                        Full-resolution preview for the selected row.<br/>Changes here also update the grid thumbnail.
                      </div>
                    </div>
                  )}
                  {insTab==='info' && (
                    <>
                      <div className="pi2-insect">
                        <div className="pi2-sectitle">Additional Info <button type="button" className="pi2-addfld" style={{fontSize:9.5}}><span>+</span>Add Field</button></div>
                        {[['remarks','Remarks'],['payDueDate','Payment Due Date'],['payDisc','Payment Discount %'],['cashDisc','Cash Discount %']].map(([k,lbl])=>(
                          <div className="pi2-edrow" key={k}><label>{lbl}</label>
                            <input value={insVals[k]||''} onChange={e=>handleInsChange(k,e.target.value)} />
                          </div>
                        ))}
                        <div className="pi2-edrow-footer">
                          <button className="pi2-btn" type="button" style={{fontSize:11}}>📜 Supplier History</button>
                          <button className="pi2-btn" type="button" style={{fontSize:11,marginLeft:4}}>📋 Pending Bills</button>
                          <button className="pi2-btn" type="button" style={{fontSize:11,marginLeft:4}}>🤖 Purchase Analysis (AI)</button>
                        </div>
                      </div>
                      <div className="pi2-insect">
                        <div className="pi2-sectitle">Against P.O</div>
                        {[['poNo','P.O No'],['ordQty','Ordered Qty'],['pendQty','Pending Qty']].map(([k,lbl])=>(
                          <div className="pi2-edrow" key={k}><label>{lbl}</label>
                            <input value={insVals[k]||'—'} disabled />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Rail actions */}
          <div className="pi2-rail-actions">
            <button type="button" className="pi2-btn primary" onClick={handleSave} data-testid="save-btn">
              Save <span className="pi2-kbd">F8</span>
            </button>
            <button type="button" className="pi2-btn" onClick={()=>setShowPrint(true)} data-testid="print-btn">
              🖨 Print <span className="pi2-kbd">F9</span>
            </button>
            <button type="button" className="pi2-btn" onClick={handleClear}>Clear</button>
            {onBack&&(
              <button type="button" className="pi2-btn" onClick={onBack} data-testid="pi-back-btn">
                ← Back to List
              </button>
            )}
            <button type="button" className="pi2-btn" onClick={()=>{setSelIdx(null);setInsVals({});}}
              data-testid="deselect-btn">Clear Selection <span style={{fontSize:9}}>Esc</span></button>
          </div>
        </div>
      </div>{/* /body */}

      {/* Status bar */}
      <div className="pi2-statusbar">
        <span>Ready…</span>
        <span>Branch: <b>Main Branch</b></span>
        <span>User: <b>SSADMIN</b></span>
        <span>Business day: <b>29-Aug-2026</b></span>
        <span>Screen Id: <b>{cfg.sid}</b></span>
        <span>Time: <b>{clock}</b></span>
      </div>

      {/* Toast */}
      <div className={`pi2-toast${toastVis?'':' hidden'}`}>{toast}</div>

      {/* Print */}
      {showPrint && (
        <PurchaseInvoicePrint
          cfg={cfg}
          hdr={hdr}
          rows={rows}
          bill={bill}
          totals={{ grossAmt, discAmt, totGST, netAmt, cgstAmt, sgstAmt, freightGST }}
          company={null}
          onClose={()=>setShowPrint(false)}
        />
      )}

      {/* Modals */}
      {showNewItem && (
        <NewItemModal
          initialValues={editRow}
          onSave={row=>{
            if(editRow&&selIdx!==null){
              setRows(p=>p.map((r,i)=>i===selIdx?{...r,...row,_id:r._id}:r));
              showToast('Row updated');
            } else {
              setRows(p=>[...p,row]);
              showToast('Item added to grid');
            }
          }}
          onClose={()=>{ setShowNewItem(false); setEditRow(null); }}
        />
      )}
      {showSizeCfg && (
        <SizeConfigModal
          selRow={selRow}
          onSaveAll={newRows=>{ setRows(p=>[...p,...newRows]); showToast(`${newRows.length} size rows added`); }}
          onClose={()=>setShowSizeCfg(false)}
        />
      )}

      {/* GetInfo Popup */}
      {popup && !showNewItem && !showSizeCfg && (
        <GetInfoPopup
          screenId={popup.si.screenId}
          controlId={String(popup.si.controlId)}
          title={popup.title}
          onSelect={handleLkpSelect}
          onClose={()=>setPopup(null)}
        />
      )}
      {/* Row context menu modals */}
      {histModal?.type==='item'&&<ItemHistoryModal row={histModal.row} onClose={()=>setHistModal(null)}/>}
      {histModal?.type==='supplier'&&<SupplierHistoryModal row={histModal.row} onClose={()=>setHistModal(null)}/>}
      {imeiModal&&<SplitBarcodeModal row={imeiModal} onSave={()=>{}} onClose={()=>setImeiModal(null)}/>}
      {imgModal&&<ImageManagerModal
        itemKey={imgModal.row?.code||`row_${imgModal.row?._id}`}
        itemLabel={imgModal.row?.name||'Item'}
        onSave={(ref)=>{ setRows(p=>p.map((r,i)=>i===imgModal.idx?{...r,_imgUrl:ref.firstThumb,_imgRef:ref.imageKey}:r)); }}
        onClose={()=>setImgModal(null)}/>}
      {imeiModal2&&<UnitIdentifierModal row={imeiModal2}
        onSave={data=>showToast(`Unit data saved for ${data.units?.length} unit(s)`)}
        onClose={()=>setImeiModal2(null)}/>}
    </div>
  );
}


// ─── Default export: list → form ───────────────────────────────────────────
export default function PurchaseInvoiceV2Screen({ screenId, theme, navigateTo }) {
  const [view,   setView]   = useState('list');
  const [editId, setEditId] = useState(null);

  if (view === 'form') {
    return (
      <PIFormContent
        screenId={screenId}
        theme={theme}
        navigateTo={navigateTo}
        onBack={()=>{ setView('list'); setEditId(null); }}
      />
    );
  }
  return (
    <PIList
      screenId={screenId}
      onNewEntry={()=>setView('form')}
      onEditEntry={r=>{ setEditId(r.id||null); setView('form'); }}
      onBack={navigateTo ? ()=>navigateTo('HUB') : undefined}
    />
  );
}
